import React from "react";
import HealthCard from "./HealthCard";
import { formatHoursToDuration, formatNumber } from "../utils";

/**
 * fmd/22 data:
 * {
 *   totalVehicles,
 *   averageWorkshopTatHours,
 *   averageWorkshopTatBreakdown, // e.g. "0 d 0 h 0 m 0 s"
 *   completedServicesCount
 * }
 */
const parseBreakdown = (breakdown, hours) => {
  if (typeof breakdown === "string" && breakdown.trim()) {
    // "0 d 0 h 0 m 0 s" → main: "0 d 0 h", sub: "0 m 0 s"
    const parts = breakdown.trim().split(/\s+/);
    if (parts.length >= 8) {
      return {
        main: `${parts[0]} ${parts[1]} ${parts[2]} ${parts[3]}`,
        sub: `${parts[4]} ${parts[5]} ${parts[6]} ${parts[7]}`,
      };
    }
    if (parts.length >= 4) {
      return {
        main: `${parts[0]} ${parts[1]} ${parts[2]} ${parts[3]}`,
        sub: parts.slice(4).join(" "),
      };
    }
    return { main: breakdown.trim(), sub: "" };
  }
  return formatHoursToDuration(hours);
};

const WorkshopTat = ({ translate, data, loading }) => {
  const totalVehicles = Number(data?.totalVehicles ?? 0);
  const hours = Number(data?.averageWorkshopTatHours ?? 0);
  const completedJobs = Number(data?.completedServicesCount ?? 0);
  const minSample = 10;
  const targetHours = 12;
  const duration = parseBreakdown(data?.averageWorkshopTatBreakdown, hours);
  const progress = Math.min(100, (hours / targetHours) * 100);
  const belowThreshold = completedJobs < minSample;
  const hasSample = completedJobs > 0;

  return (
    <HealthCard
      className="fh-span-3"
      title={translate("averageWorkshopTat")}
      subtitle={translate("averageWorkshopTatSub")}
      // badge={
      //   totalVehicles
      //     ? `${formatNumber(totalVehicles)} ${translate("total")}`
      //     : undefined
      // }
      badgeStyle={{ background: "#e8f1fa", color: "#2f8fd8" }}
      loading={loading}
    >
      <div style={{ padding: "6px 0 8px" }}>
        <div className="fh-kpi">
          {duration.main} {duration.sub ? <small>{duration.sub}</small> : null}
        </div>
        {hasSample ? (
          <div
            className={`fh-delta ${hours <= targetHours ? "down" : "up"}`}
            style={{
              marginTop: 2,
              color: hours <= targetHours ? "#3f9c40" : "#e0484c",
            }}
          >
            {hours <= targetHours
              ? `▼ ${translate("wellInsideTarget")} (${targetHours} h)`
              : `▲ ${translate("aboveTarget")} (${targetHours} h)`}
          </div>
        ) : (
          <div className="fh-delta" style={{ marginTop: 2, color: "#8a97a3" }}>
            {translate("noRecordFound")}
          </div>
        )}
      </div>
      <div className="fh-track" style={{ height: 6, marginBottom: 8 }}>
        <div
          className="fh-fill"
          style={{
            width: `${hasSample ? progress : 0}%`,
            background: "#2f8fd8",
          }}
        />
      </div>
      <div className="fh-metaline">
        <span>{translate("totalVehicles")}</span>
        <b>{formatNumber(totalVehicles)}</b>
      </div>
      <div className="fh-metaline">
        <span>{translate("completedJobsMeasured")}</span>
        <b>{formatNumber(completedJobs)}</b>
      </div>
      {belowThreshold ? (
        <div className="fh-metaline">
          <span className="flag">{translate("sampleBelowThreshold")}</span>
          <b className="flag">
            {translate("min")} {minSample}
          </b>
        </div>
      ) : null}
    </HealthCard>
  );
};

export default WorkshopTat;
